import React from 'react';
import { Link } from 'react-router-dom';
import { Check, TestTubes, HeartPulse, ShieldCheck, Lock, Clock, User } from 'lucide-react';
import { IMAGES, SERVICES, AURELIA_STANDARDS, CLIENT_TYPES } from '../data/mock';
import { SERVICE_SLUGS, COMING_SOON_SLUGS } from '../data/serviceDetails';

const ICONS = { TestTubes, HeartPulse, ShieldCheck, Lock, Clock, User };

const Services = () => {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0f1113] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-90"
          style={{ backgroundImage: `url(${IMAGES.medicalBag})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1113] via-[#0f1113]/85 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-28">
          <div className="max-w-xl">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] mb-6 font-medium">
              OUR SERVICES
            </p>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Premium Care.<br />On Your Terms.
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              We bring the lab to you—delivering exceptional phlebotomy and CPR training services with unmatched convenience, discretion, and professionalism.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-[#f5f0ea] pt-20 pb-10 md:pt-24">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-5">
            SERVICES DESIGNED FOR YOUR LIFESTYLE
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1c1e20] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Clinical Excellence. Concierge Experience.
          </h2>
          <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-6" />
          <p className="text-[#4a4a4a] leading-relaxed text-[15px] max-w-2xl mx-auto">
            Whether you need expert lab services in the comfort of your home or facility or life-saving training for yourself or your team, Aurelia ensures a seamless, elevated experience from start to finish.
          </p>
        </div>
      </section>

      {/* Service cards */}
      <section className="bg-[#f5f0ea] pb-20">
        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
          {SERVICES.map((svc) => {
            const slug = SERVICE_SLUGS[svc.title];
            const comingSoon = COMING_SOON_SLUGS.has(slug);
            return (
              <div
                key={svc.title}
                data-testid={`service-card-${slug}`}
                className={`bg-white border border-black/5 shadow-sm relative pb-8 ${comingSoon ? 'opacity-95' : ''}`}
              >
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className={`w-full h-full object-cover ${comingSoon ? 'grayscale-[35%]' : ''}`}
                  />
                  {comingSoon && (
                    <div
                      data-testid={`coming-soon-badge-${slug}`}
                      className="absolute top-4 right-4 bg-[#141618] text-[#c9a961] border border-[#c9a961] tracking-[0.24em] text-[10px] font-semibold px-3 py-1.5 shadow-md"
                    >
                      COMING SOON
                    </div>
                  )}
                </div>
                <div className="px-8 pt-10 text-center">
                  <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-3">
                    {svc.number}
                  </p>
                  <h3 className="font-serif text-3xl text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                    {svc.title}
                  </h3>
                  <div className="w-12 h-[2px] bg-[#c9a961] mx-auto mb-5" />
                  <p className="text-[#4a4a4a] text-[14px] leading-relaxed mb-6 text-left">
                    {svc.description}
                  </p>
                  <ul className="space-y-2 mb-8 text-left">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-[14px] text-[#3c3c3c]">
                        <Check size={16} className="text-[#c9a961] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {comingSoon ? (
                    <Link
                      to={`/services/${slug}`}
                      data-testid={`service-card-cta-${slug}`}
                      className="inline-block border border-[#c9a961] bg-[#faf6f0] text-[#8a6d2c] tracking-[0.22em] text-[11px] font-semibold px-8 py-3 cursor-default"
                    >
                      COMING SOON
                    </Link>
                  ) : (
                    <Link
                      to={`/services/${slug}`}
                      data-testid={`service-card-cta-${slug}`}
                      className="inline-block border border-[#1c1e20] hover:bg-[#1c1e20] hover:text-white transition-colors duration-300 text-[#1c1e20] tracking-[0.22em] text-[11px] font-semibold px-8 py-3"
                    >
                      LEARN MORE
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Who We Serve */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">WHO WE SERVE</p>
            <h2
              className="font-serif text-4xl md:text-5xl text-[#1c1e20] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Trusted partners across every industry.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-4" />
            <p className="text-[#4a4a4a] leading-relaxed text-[15px] max-w-2xl mx-auto">
              Aurelia proudly serves individuals, families, and organizations of every size.
            </p>
          </div>
          <div className="bg-[#faf6f0] border border-black/5 p-6 md:p-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
              {CLIENT_TYPES.map((c) => (
                <li key={c} className="flex items-start gap-2 py-1.5 border-b border-black/5 last:border-b-0">
                  <Check size={14} className="text-[#c9a961] mt-1 flex-shrink-0" strokeWidth={2} />
                  <span className="text-[#3c3c3c] text-[14px]">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Aurelia Standard */}
      <section className="bg-[#f5f0ea] pb-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="bg-[#faf6f0] border border-black/5 py-12 px-6">
            <p className="text-center text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-10">
              THE AURELIA STANDARD
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-y md:divide-y-0 md:divide-x divide-black/10">
              {AURELIA_STANDARDS.map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <div key={s.title} className="px-4 flex flex-col items-center text-center">
                    <Icon size={28} className="text-[#c9a961] mb-4" strokeWidth={1.3} />
                    <h3 className="text-[12px] tracking-[0.24em] font-semibold text-[#1c1e20] mb-3">{s.title}</h3>
                    <p className="text-[#4a4a4a] text-[13px] leading-relaxed">{s.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#141618] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.luxuryInterior})` }}
        />
        <div className="absolute inset-0 bg-[#141618]/85" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <h3 className="font-serif text-3xl md:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Elevated care.<br />Exactly where you need it.
          </h3>
          <div>
            <p className="text-white/80 mb-5 max-w-md">
              Experience the convenience, discretion, and professionalism you deserve.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-7 py-3.5"
            >
              BOOK YOUR APPOINTMENT
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
